<template>
  <div class="pi-aid">
    <dl class="pi-aid__facts">
      <div class="pi-aid__fact">
        <dt>Quando</dt>
        <dd>{{ detail.askedAtText }}<span class="pi-table__sub">terminou {{ detail.finishedAtText }}</span></dd>
      </div>
      <div class="pi-aid__fact">
        <dt>Quem enviou</dt>
        <dd>{{ detail.userName }}<span v-if="detail.userEmail" class="pi-table__sub">{{ detail.userEmail }}</span></dd>
      </div>
      <div class="pi-aid__fact">
        <dt>Status<pi-definition-tooltip class="pi-aid__info" info-key="assistant.answerStatus" label="Status"/></dt>
        <dd><span class="pi-badge" :class="'pi-badge--' + detail.status.tone">{{ detail.status.label }}</span></dd>
      </div>
      <div class="pi-aid__fact">
        <dt>Agente</dt>
        <dd>{{ detail.agentText }}<span class="pi-table__sub">{{ detail.routeText }}</span></dd>
      </div>
      <div class="pi-aid__fact">
        <dt>Conversa</dt>
        <dd><code class="pi-aid__code">{{ detail.conversationText }}</code></dd>
      </div>
      <div class="pi-aid__fact">
        <dt>Avaliação</dt>
        <dd>
          <span v-if="detail.feedback" class="pi-badge" :class="'pi-badge--' + detail.feedback.tone">{{ detail.feedback.label }}</span>
          <span v-else>Sem avaliação</span>
          <span v-if="detail.feedbackComment" class="pi-table__sub">“{{ detail.feedbackComment }}”</span>
          <span v-if="detail.feedbackAtText" class="pi-table__sub">em {{ detail.feedbackAtText }}</span>
        </dd>
      </div>
      <div v-for="row in detail.totals" :key="row.key" class="pi-aid__fact pi-aid__fact--wide">
        <dt>{{ row.label }}<pi-definition-tooltip v-if="row.infoKey" class="pi-aid__info" :info-key="row.infoKey" :label="row.label"/></dt>
        <dd>{{ row.value }}</dd>
      </div>
    </dl>
    <p v-if="detail.unverifiedText" class="pi-warn">{{ detail.unverifiedText }} na resposta: confira na tela do PI.</p>

    <section class="pi-aid__section" aria-labelledby="pi-aid-question">
      <h3 id="pi-aid-question" class="pi-subtitle">Pergunta enviada (entrada)</h3>
      <p class="pi-aid__question">{{ detail.question }}</p>
      <p class="pi-muted">Gravada e enviada ao modelo com CPF, e-mail e telefone mascarados.</p>
      <template v-if="detail.contextText">
        <h4 class="pi-aid__label">Contexto da tela</h4>
        <pre class="pi-aid__pre">{{ detail.contextText }}</pre>
      </template>
    </section>

    <section class="pi-aid__section" aria-labelledby="pi-aid-answer">
      <h3 id="pi-aid-answer" class="pi-subtitle">Resposta (saída)</h3>
      <p v-if="detail.errorText" class="pi-aid__error" role="status">Erro: {{ detail.errorText }}</p>
      <div v-if="detail.answer" class="pi-aid__answer">
        <pi-assistant-answer :body="detail.answer" :status="detail.status" :show-status="true" :warnings="detail.warnings"
                             :export-context="detail.exportContext"/>
        <pi-assistant-trace :steps="detail.trace" :open="true"/>
      </div>
      <template v-else>
        <p class="pi-muted">{{ detail.purged ? purgedText : 'Sem resposta gravada.' }}</p>
        <div v-for="warning in detail.warnings" :key="warning.key" class="pi-warn">{{ warning.text }}</div>
      </template>
    </section>

    <section class="pi-aid__section" aria-labelledby="pi-aid-calls">
      <div class="pi-aid__head">
        <h3 id="pi-aid-calls" class="pi-subtitle">Chamadas ao modelo ({{ detail.modelCalls.length }})</h3>
        <pi-export-menu :title="'Chamadas da pergunta ' + detail.id" :screen="screenName" :columns="modelCallColumns" :rows="detail.modelCallSource"
                        :filters="detail.exportContext.filters" :period="detail.exportContext.period" :register="false"
                        :disabled="!detail.modelCalls.length"/>
      </div>
      <div v-if="detail.modelCalls.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Chamadas ao modelo desta pergunta</caption>
          <thead>
          <tr>
            <pi-th v-for="column in modelCallHeaders" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="call in detail.modelCalls" :key="call.key" :title="'Início: ' + call.startedText">
            <td>#{{ call.index }}</td>
            <td>
              {{ call.purposeText }}
              <span v-if="call.modelShown" class="pi-table__sub">{{ call.model }}</span>
              <span v-if="call.promptHash" class="pi-table__sub">prompt {{ call.promptHash }}</span>
              <span v-if="call.errorCode" class="pi-table__sub pi-aid__bad">{{ call.errorCode }}</span>
            </td>
            <td class="is-num">{{ call.messagesText }}</td>
            <td class="is-num">{{ call.inputText }}</td>
            <td class="is-num">{{ call.cachedText }}</td>
            <td class="is-num">{{ call.outputText }}</td>
            <td>{{ call.finishText }}</td>
            <td class="is-num">{{ call.toolCallsText }}</td>
            <td class="is-num">{{ call.costText }}</td>
            <td class="is-num">{{ call.latencyText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma chamada ao modelo.</p>
    </section>

    <section class="pi-aid__section" aria-labelledby="pi-aid-tools">
      <div class="pi-aid__head">
        <h3 id="pi-aid-tools" class="pi-subtitle">Consultas ao PI ({{ detail.toolCalls.length }})</h3>
        <pi-export-menu :title="'Consultas da pergunta ' + detail.id" :screen="screenName" :columns="toolCallColumns" :rows="detail.toolCallSource"
                        :filters="detail.exportContext.filters" :period="detail.exportContext.period" :register="false"
                        :disabled="!detail.toolCalls.length"/>
      </div>
      <div v-if="detail.toolCalls.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Ferramentas chamadas pelo modelo</caption>
          <thead>
          <tr>
            <pi-th v-for="column in toolCallHeaders" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="call in detail.toolCalls" :key="call.key">
            <td>{{ call.callText }}<span class="pi-table__sub">{{ call.toolCallId }}</span></td>
            <td>
              <code class="pi-aid__code pi-aid__tool">{{ call.tool }}</code>
              <span v-if="call.endpoint" class="pi-table__sub">{{ call.endpoint }}</span>
              <span v-if="call.dataText" class="pi-table__sub">{{ call.dataText }}</span>
            </td>
            <td><pre v-if="call.argumentsText" class="pi-aid__pre pi-aid__pre--small">{{ call.argumentsText }}</pre><span v-else>—</span></td>
            <td>
              <span class="pi-badge" :class="'pi-badge--' + call.status.tone">{{ call.status.label }}</span>
              <span v-if="call.errorMessage" class="pi-table__sub pi-aid__bad">{{ call.errorMessage }}</span>
            </td>
            <td class="is-num">{{ call.rowsText }}</td>
            <td class="is-num">{{ call.charsText }}</td>
            <td class="is-num">{{ call.tokensText }}</td>
            <td>{{ call.truncatedText }}</td>
            <td class="is-num">{{ call.latencyText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma ferramenta chamada.</p>
    </section>

    <section class="pi-aid__section" aria-labelledby="pi-aid-transcript">
      <h3 id="pi-aid-transcript" class="pi-subtitle">Transcrição completa</h3>
      <p v-if="detail.transcriptText" class="pi-muted">{{ detail.transcriptText }}</p>
      <template v-else>
        <p class="pi-muted">A entrada da chamada #k são as mensagens marcadas com ela; a saída é a mensagem logo depois. A pergunta do roteador não entra aqui.</p>
        <details v-for="message in detail.transcript" :key="message.key" class="pi-aid__message">
          <summary class="pi-aid__message-head">
            <span class="pi-aid__message-role">{{ message.index }} · {{ message.roleLabel }}</span>
            <span v-for="mark in message.marks" :key="mark.key" class="pi-badge" :class="'pi-badge--' + mark.tone">{{ mark.text }}</span>
            <span v-if="message.toolCallId" class="pi-aid__message-id">{{ message.toolCallId }}</span>
          </summary>
          <p v-if="message.note" class="pi-muted">{{ message.note }}</p>
          <pre v-if="message.content" class="pi-aid__pre">{{ message.content }}</pre>
          <div v-for="call in message.toolCalls" :key="call.key" class="pi-aid__tool-request">
            <span class="pi-aid__label">Pediu {{ call.name }} <span class="pi-aid__message-id">{{ call.id }}</span></span>
            <pre class="pi-aid__pre pi-aid__pre--small">{{ call.argumentsText }}</pre>
          </div>
        </details>
      </template>
    </section>

    <section v-if="detail.systemPrompt" class="pi-aid__section" aria-labelledby="pi-aid-prompt">
      <h3 id="pi-aid-prompt" class="pi-subtitle">Prompt de sistema</h3>
      <details class="pi-aid__message">
        <summary class="pi-aid__message-head">Mostrar o prompt (hash {{ detail.systemPromptHash }})</summary>
        <pre class="pi-aid__pre">{{ detail.systemPrompt }}</pre>
      </details>
    </section>

    <section class="pi-aid__section" aria-labelledby="pi-aid-meta">
      <h3 id="pi-aid-meta" class="pi-subtitle">Modelo, implantação e versões</h3>
      <dl class="pi-aid__facts">
        <div v-for="row in detail.meta" :key="row.key" class="pi-aid__fact">
          <dt>{{ row.label }}</dt>
          <dd><code class="pi-aid__code">{{ row.value }}</code></dd>
        </div>
      </dl>
    </section>
  </div>
</template>

<script>
import piAssistantAnswer from "./piAssistantAnswer.vue";
import piAssistantTrace from "./piAssistantTrace.vue";
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import piExportMenu from "./piExportMenu.vue";
import piTh from "./piTh.vue";
import { screenColumns } from "./piColumns.js";
import { MODEL_CALL_COLUMNS, PURGED_TEXT, TOOL_CALL_COLUMNS, USAGE_SCREEN } from "./piAssistantUsageModel.js";

export default {
  name: "pi-assistant-usage-detail",
  components: { piAssistantAnswer, piAssistantTrace, piDefinitionTooltip, piExportMenu, piTh },
  props: {
    detail: { type: Object, required: true },
  },
  data() {
    return {
      purgedText: PURGED_TEXT,
      screenName: USAGE_SCREEN,
      modelCallColumns: MODEL_CALL_COLUMNS,
      toolCallColumns: TOOL_CALL_COLUMNS,
      modelCallHeaders: screenColumns(MODEL_CALL_COLUMNS),
      toolCallHeaders: screenColumns(TOOL_CALL_COLUMNS),
    };
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-aid__facts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 16px;
  margin: 0 0 14px;
}

.pi-aid__fact {
  min-width: 0;
}

.pi-aid__fact--wide {
  grid-column: 1 / -1;
}

.pi-aid__fact dt {
  font-size: 11px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
}

.pi-aid__fact dd {
  margin: 2px 0 0;
  font-size: 13px;
  color: #1f2933;
  word-break: break-word;
}

.pi-aid__section {
  margin-bottom: 20px;
}

.pi-aid__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 12px;
  margin-bottom: 6px;
}

.pi-aid__head .pi-subtitle {
  margin-bottom: 0;
}

.pi-aid__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-aid__question {
  margin: 0 0 4px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #eef3fe;
  color: #1f2933;
  font-size: 13px;
  white-space: pre-line;
  word-break: break-word;
}

.pi-aid__answer {
  padding: 12px 14px;
  border: 1px solid #e3e8f0;
  border-radius: 6px;
}

.pi-aid__label {
  display: block;
  margin: 8px 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
}

.pi-aid__pre {
  max-height: 360px;
  margin: 4px 0 8px;
  padding: 8px 10px;
  overflow: auto;
  border: 1px solid #e6e7eb;
  border-radius: 4px;
  background: #f8f9fb;
  color: #1f2933;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.pi-aid__pre--small {
  min-width: 180px;
  max-width: 320px;
  max-height: 160px;
  margin: 0;
  white-space: pre;
  word-break: normal;
}

.pi-aid__code {
  padding: 0;
  background: none;
  color: #1f2933;
  font-size: 12px;
  word-break: break-all;
}

.pi-aid__tool {
  white-space: nowrap;
  word-break: normal;
}

.pi-aid__bad {
  color: #a3172b;
}

.pi-aid__error {
  margin: 0 0 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fdecee;
  color: #a3172b;
  font-size: 12px;
}

.pi-aid__message {
  margin-bottom: 6px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  padding: 6px 10px;
  background: #ffffff;
}

.pi-aid__message-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}

.pi-aid__message-role {
  font-weight: 700;
  color: #1f2933;
}

.pi-aid__message-id {
  color: #6b7280;
  font-size: 11px;
  font-weight: 400;
}

.pi-aid__tool-request {
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piAssistantUsageDetail.vue